import { useState, useEffect, useCallback, useRef } from "react";
import { RiErrorWarningLine } from "react-icons/ri";
import { v4 as uuidv4 } from "uuid";
import SideBarContent from "./sidebar";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { HiMenuAlt1 } from "react-icons/hi";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";
import { Dropdown, Button } from "antd";
import Modal from "../../components/Modal";

import IncidentType from "../../screens/User/Users/components/IncidentType";
import NextIncidentLog from "../../screens/User/Users/components/nextLogIncident";
import "./adminLayout.css";
import {
  AdminLayoutWrapper,
  BellandAvatarContainer,
  CurrentContentContainer,
  // DropDown,
  DropDownContainer,
  DropDownItem,
  HeaderContainer,
  MainContent,
  NotificationandAvatarContainer,
  StyledAvatar,
  SidebarContainer,
} from "./AdminLayout.style";
import { HideOnDesktop } from "../incidentLayout/incident.style";

import moment from "moment";

import { clearStorage, useAdminWrapper, useStoreWrapper } from "../../store/store";
import useUser from "../../screens/User/hooks/useUser";
import { AssModal } from "../../screens/User/Users/components/Roles/style";

const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes in milliseconds

interface Props {
  children?: React.ReactNode;
}

const AdminLayout: React.FC<Props> = ({ children }) => {
  const {
    user,
    loggedinUserImage,
    userFacilities,
    setLoggedinUserImage,
    openIncidentClicked,
    setOpenIncidentClicked,
    createForm,
    updateForm,
    mode,
  } = useStoreWrapper((state) => ({
    user: state.user,
    loggedinUserImage: state.loggedinUserImage,
    userFacilities: state.userFacilities,
    setLoggedinUserImage: state.setLoggedinUserImage,
    openIncidentClicked: state.openIncidentClicked,
    setOpenIncidentClicked: state.setOpenIncidentClicked,
    createForm: state.createForm,
    updateForm: state.updateForm,
    mode: state.mode,
  }));

  const { expanded, setExpanded } = useAdminWrapper((state) => ({
    expanded: state.expanded,
    setExpanded: state.setExpanded,
  }));

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const toggleDrawer = () => {
    setIsDrawerOpen((prevState) => !prevState);
  };
  const location = useLocation();
  const { handleGetLoggedinUserImage } = useUser();

  const { data: getUserImage } = handleGetLoggedinUserImage;

  const [inactive, setInactive] = useState<boolean>(false);
  const [seconds, setSeconds] = useState<number>();

  const [openModal, setOpenModal] = useState(false);
  const [openNextLogModal, setOpenNextLogModal] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const handleCloseDropdown = useCallback(() => {
    if (showDropdown) {
      setShowDropdown(false);
    } else {
      return;
    }
  }, [showDropdown, setShowDropdown]);

  // @ts-ignore

  const navigate = useNavigate();
  const isGuest = userFacilities[0]?.profileName === "Guest User";
  const [avatarBase64, setAvatarBase64] = useState<string>("");
  const [showWarning, setShowWarning] = useState(false);
  const idempotencyKey = useRef(uuidv4());

  function openFirstModal() {
    setOpenModal(true);
  }

  const handleSubmit = (payload: any) => {
    if (!idempotencyKey.current) {
      idempotencyKey.current = uuidv4();
    }
    createForm(idempotencyKey.current);
    const baseFormValues: any = {
      facilityId: payload.facilityId,
      incidentType: payload.incidentType.id,
      idempotencyKey: idempotencyKey.current,
    };
    if (!isGuest) {
      baseFormValues["residentId"] = payload.residentId;
    }
    try {
      updateForm(idempotencyKey.current, {
        type: "UPDATE_BASE_FORM_DATA",
        payload: baseFormValues,
      });
    } catch (error) {
      console.log("Error with updating state");
    }
    setOpenIncidentClicked(false);
  };

  //for the NEXT button on the first incident log modal
  const handleIncidentLog = (payload: any) => {
    handleSubmit(payload);
    if (!isGuest) {
      navigate(`/incidents/${idempotencyKey.current}/patient-demographics`);
    } else {
      setOpenNextLogModal(true);
    }
    setOpenIncidentClicked(false);
  };

  //for the NEXT button on the second incident log modal when user is a guest user
  const handleLogNextIncident = (payload: any) => {
    try {
      updateForm(idempotencyKey.current, {
        type: "UPDATE_BASE_FORM_DATA",
        payload: {
          guestName: payload.name,
          agency: payload.agency,
          shiftWorked: payload.shiftWorked,
          unitAssignment: payload.unitAssignment,
          ssnLastFourDigits: payload.SSN,
        },
      });
      setOpenIncidentClicked(false);
      navigate(`/incidents/${idempotencyKey.current}/patient-demographics`);
    } catch (error) {
      console.log("Error with updating state with Id");
    }
  };

  const [showSideBar, setShowSideBar] = useState(false);
  // to prevent performance issues we wrap the function in useCallback to return a memoized version of the callback that only changes if one of the dependencies has changed

  const handleSidebarClose = useCallback(() => {
    if (showSideBar) {
      setShowSideBar(false);
    } else {
      return;
    }
  }, [showSideBar, setShowSideBar]);

  let inactiveTimer: any;
  let logoutTimer: any;

  const handleUserAction = useCallback(() => {
    setInactive(false);
    clearTimeout(inactiveTimer);
    clearTimeout(logoutTimer);

    inactiveTimer = setTimeout(() => {
      setInactive(true);
      // Start logout timer when inactive
      logoutTimer = setTimeout(() => {
        // Handle logout logic here
        localStorage.clear();
        const lastPath = window.location.pathname;
        if (lastPath) window.location.replace(`/?lastPath=${lastPath}`);
        console.log("Countdown finished!");
      }, INACTIVITY_TIMEOUT / 2); // Logout after additional 30 minutes
    }, INACTIVITY_TIMEOUT);
  }, [navigate]);

  useEffect(() => {
    // Add event listeners for user activity
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
    ];
    events.forEach((event) => window.addEventListener(event, handleUserAction));

    // Initial timer setup
    handleUserAction();

    // Cleanup
    return () => {
      events.forEach((event) =>
        window.removeEventListener(event, handleUserAction)
      );
      clearTimeout(inactiveTimer);
      clearTimeout(logoutTimer);
    };
  }, [handleUserAction]);

  useEffect(() => {
    const countdown = moment.duration(INACTIVITY_TIMEOUT / 2, "milliseconds");
    let intervalId: any;
    if (inactive) {
      intervalId = setInterval(() => {
        setSeconds(countdown.asSeconds());
        countdown.subtract(1, "second");

        if (countdown.asSeconds() <= 0) {
          clearInterval(intervalId);
          localStorage.clear();
          const lastPath = window.location.pathname;
          if (lastPath) window.location.replace(`/?lastPath=${lastPath}`);
          console.log("Countdown finished!");
        }
      }, 1000);
    }
    return () => {
      clearInterval(intervalId);
    };
  }, [inactive]);

  useEffect(() => {
    if (loggedinUserImage!.length > 0) {
      const base64String = /^(?!data:)/i.test(loggedinUserImage!)
        ? "data:image/jpeg;base64," + loggedinUserImage!
        : loggedinUserImage!;
      setAvatarBase64(base64String);
    }
  }, [loggedinUserImage]);

  useEffect(() => {
    const imageString = getUserImage?.data.data || "";
    setLoggedinUserImage(imageString);
  }, [getUserImage]);

  useEffect(() => {
    if (mode?.maintenanceModeEnabled || mode?.deploymentModeEnabled) {
      setShowWarning(true);
    }
  }, [mode]);

  useEffect(() => {
    if (openIncidentClicked) {
      openFirstModal();
    }
  }, [openIncidentClicked]);

  document.body.style.overflow = "hidden";

  const avatarDropDownOptions = [
    {
      key: "1",
      label: (
        <Button
          className="flex w-full items-center border-0"
          onClick={() => {
            navigate("/user/settings");
            setShowDropdown(false);
          }}
        >
          Settings
        </Button>
      ),
    },
    {
      key: "2",
      label: (
        <Button
          className="flex w-full items-center border-0"
          onClick={() => {
            clearStorage();
            localStorage.clear();
            setShowDropdown(false);
            navigate("/");
          }}
        >
          Logout
        </Button>
      ),
    },
  ];
  const handleShowDropDown = () => setShowDropdown(!showDropdown);
  const handleCloseDropDown = () => setShowDropdown(false);

  const handleExpand = () => setExpanded(!expanded);

  return (
    // @ts-ignore
    <AdminLayoutWrapper>
      <SidebarContainer>
        <SideBarContent
          onOpenIncidentModal={openFirstModal}
          onSetExpanded={handleExpand}
          expanded={expanded}
          showContent
        />
      </SidebarContainer>

      <MainContent
        style={{
          backgroundColor:
            location.pathname === "/user/dashboard" ? "whitesmoke" : "",
        }}
        onClick={() => {
          handleSidebarClose();
          handleCloseDropdown();
        }}
      >
        <HeaderContainer>
          <HideOnDesktop>
            <HiMenuAlt1
              size={40}
              style={{ cursor: "pointer" }}
              onClick={() => setIsDrawerOpen(true)}
            />
          </HideOnDesktop>

          <Drawer
            z-index="5"
            open={isDrawerOpen}
            onClose={toggleDrawer}
            direction="left"
            style={{ width: "fit-content", minWidth: "300px" }}
          >
            <SideBarContent
              onSideBarItemClick={handleCloseDropDown}
              onSelectItem={() => setIsDrawerOpen(false)}
              onOpenIncidentModal={openFirstModal}
              onSetExpanded={handleExpand}
              expanded={expanded}
            />
          </Drawer>

          <NotificationandAvatarContainer
            className={`w-full ${showWarning ? "justify-between" : "justify-end"}`}
          >
            {showWarning && (
              <div
                role="alert"
                className="flex-start flex w-full items-center rounded-b border border-t-0 border-red-400 bg-red-100 px-4 py-3 text-red-700 sm:w-fit"
              >
                <RiErrorWarningLine size={20} className="mr-2" />
                <p>System maintenance in progress!</p>
              </div>
            )}
            <Dropdown menu={{ items: avatarDropDownOptions }}>
              <BellandAvatarContainer className="w-fit justify-end">
                <DropDownContainer
                  show={showDropdown}
                  where={location.pathname}
                ></DropDownContainer>
                {loggedinUserImage!.length > 0 ? (
                  <StyledAvatar
                    src={avatarBase64}
                    onClick={handleShowDropDown}
                  />
                ) : (
                  <StyledAvatar
                    onClick={handleShowDropDown}
                    name={`${user.firstName} ${user.lastName}`}
                    className=""
                  />
                )}
              </BellandAvatarContainer>
            </Dropdown>
          </NotificationandAvatarContainer>
        </HeaderContainer>
        <CurrentContentContainer>
          <Outlet />
          {children}
        </CurrentContentContainer>
        <Modal open={openModal}>
          <IncidentType
            handleCloseModal={() => {
              setOpenModal(false);
              setOpenIncidentClicked(false);
            }}
            isGuest={isGuest}
            handleIncidentLog={handleIncidentLog}
          />
        </Modal>

        <Modal open={openNextLogModal}>
          <NextIncidentLog
            handleCloseModal={() => {
              setOpenNextLogModal(false);
              setOpenIncidentClicked(false);
            }}
            handleIncidentLog={handleLogNextIncident}
          />
        </Modal>

        <Modal open={inactive}>
          <AssModal>
            <span className="mt-4 block text-center font-inter text-xl font-medium text-purple-600">
              You have been inactive for over{" "}
              {Math.floor(INACTIVITY_TIMEOUT / 60000)}{" "}
              {Math.floor(INACTIVITY_TIMEOUT / 60000) === 1
                ? "minute"
                : "minutes"}
              <br />
              Logout in {seconds}
              <br />
              Any action will cancel this
            </span>
          </AssModal>
        </Modal>
      </MainContent>
    </AdminLayoutWrapper>
  );
};

export default AdminLayout;
